import React from "react";
import PropTypes from "prop-types";
import { Button } from "components/common";

// ISO 20022 pain.002 status codes HSBC returns for a payment.
const HSBC_CODE = {
  RCVD: { label: "Received", tone: "info" },
  ACTC: { label: "Technically accepted", tone: "info" },
  ACCP: { label: "Accepted", tone: "info" },
  PDNG: { label: "Pending", tone: "info" },
  ACSP: { label: "Being settled", tone: "info" },
  ACWC: { label: "Accepted with change", tone: "info" },
  PART: { label: "Partly accepted", tone: "warn" },
  ACSC: { label: "Paid", tone: "ok" },
  RJCT: { label: "Rejected", tone: "error" },
};
const TONE = {
  info: "tw-bg-[#eef4ff] tw-text-geist-accent",
  ok: "tw-bg-[#edfaef] tw-text-geist-success",
  warn: "tw-bg-[#fff8ee] tw-text-[#a05e03]",
  error: "tw-bg-[#fff0f0] tw-text-geist-error",
};

const when = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) : "");

const Transactions = ({ items }) => (
  <ul className="tw-m-0 tw-mt-2 tw-p-0 tw-list-none tw-divide-y tw-divide-geist-border tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border">
    {items.map((tx, i) => (
      <li key={tx.instruction_id || i} className="tw-px-3 tw-py-2 tw-text-[12px]">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2">
          <span className="tw-font-semibold tw-text-geist-fg">{tx.instruction_id || `Transaction ${i + 1}`}</span>
          {tx.end_to_end_id && <span className="tw-text-geist-fg-3">{tx.end_to_end_id}</span>}
          <span className="tw-ml-auto tw-font-mono">{[tx.status, tx.reason_code].filter(Boolean).join(" · ")}</span>
        </div>
        {(tx.details || []).map((d) => <p key={d} className="tw-m-0 tw-mt-0.5 tw-text-geist-fg-2">{d}</p>)}
      </li>
    ))}
  </ul>
);

Transactions.propTypes = { items: PropTypes.arrayOf(PropTypes.object).isRequired };

/** What HSBC last said about a sent payment (status enquiry), and the button to ask again. */
const HsbcStatusPanel = ({ bank, canCheck, busy, onCheck }) => {
  const enquiry = bank?.enquiry;
  if (!enquiry && !canCheck) return null;

  const code = HSBC_CODE[enquiry?.hsbc_status_code];
  const reason = [enquiry?.error_code, enquiry?.error_message].filter(Boolean).join(" — ");

  return (
    <section aria-label="HSBC status" className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-p-3.5 tw-text-[13px]">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <span className="tw-font-semibold tw-text-geist-fg">HSBC status</span>
        {enquiry?.ok && enquiry.hsbc_status_code && (
          <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${TONE[code?.tone || "info"]}`}>
            {enquiry.hsbc_status_code}{code ? ` · ${code.label}` : ""}
          </span>
        )}
        <span className="tw-text-[12px] tw-text-geist-fg-3">
          {enquiry ? `Checked ${when(enquiry.checked_at)} · ${enquiry.source === "auto" ? "automatic" : "by hand"}` : "Not checked yet"}
        </span>
        {canCheck && (
          <Button size="sm" variant="secondary" className="tw-ml-auto" loading={busy} onClick={onCheck}>
            {!busy && <i className="bi bi-arrow-clockwise" />} {busy ? "Checking…" : "Check HSBC status"}
          </Button>
        )}
      </div>

      {enquiry && !enquiry.ok && (
        <p role="alert" className={`tw-m-0 tw-mt-2 tw-rounded-geist-sm tw-px-3 tw-py-2 tw-text-[12px] ${TONE.warn}`}>
          HSBC did not answer the enquiry — the status was kept. {enquiry.error}
        </p>
      )}
      {reason && <p className="tw-m-0 tw-mt-2 tw-text-[12px] tw-text-geist-fg-2">{reason}</p>}
      {(enquiry?.transactions || []).length > 0 && <Transactions items={enquiry.transactions} />}
      {enquiry?.hsbc_status_code === "RJCT" && bank.rejected_reference && (
        <p className="tw-m-0 tw-mt-2 tw-text-[12px] tw-text-geist-error">
          Nothing was paid ({bank.rejected_reference}). Fix the cause above, then use Send to HSBC again.
        </p>
      )}
      {canCheck && !bank?.auto_check && (
        <p className="tw-m-0 tw-mt-2 tw-text-[11px] tw-text-geist-fg-3">
          Automatic checks are off — turn them on in Payment Request → Settings → HSBC.
        </p>
      )}
    </section>
  );
};

HsbcStatusPanel.propTypes = {
  bank: PropTypes.object,
  canCheck: PropTypes.bool,
  busy: PropTypes.bool,
  onCheck: PropTypes.func,
};

export default HsbcStatusPanel;
